import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Activity, Mail, AlertCircle, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/services/api';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Email is required');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email');
      return;
    }

    setLoading(true);
    try {
      await api.post('/api/v1/auth/forgot-password', { email });
      setSent(true);
      toast.success('Reset link sent! Check your email.');
    } catch (err) {
      // Backend always returns success to prevent enumeration, but handle network errors
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header banner — matches Login */}
      <div className="w-full bg-primary relative overflow-hidden border-b border-primary/20">
        <div className="py-10 relative z-10">
          <motion.div
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="flex flex-col items-center gap-2"
          >
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur rounded-xl">
                <Activity className="h-7 w-7 text-white" />
              </div>
              <div className="text-left">
                <h1 className="text-3xl font-brand font-bold text-white tracking-tight">
                  Vivalyn<span className="font-normal text-clinical-soft">EMR</span>
                </h1>
                <p className="text-primary-100/80 text-sm">AI Systems for Life & HealthCare</p>
              </div>
            </div>
          </motion.div>
        </div>
        <svg className="absolute bottom-0 left-0 w-full" viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ height: '30px' }}>
          <path d="M0,40 C360,70 720,0 1080,40 C1260,55 1380,20 1440,30 L1440,60 L0,60 Z" fill="hsl(210, 25%, 97%)" />
        </svg>
      </div>

      {/* Form area */}
      <div className="flex-1 flex items-center justify-center bg-background p-4">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold text-foreground">Reset your password</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Enter your email and we'll send you a link to reset your password
            </p>
          </div>

          <Card className="border-border/80 shadow-lg">
            <CardContent className="pt-6 space-y-5">
              {sent ? (
                <div className="text-center py-6 space-y-4">
                  <div className="flex justify-center">
                    <div className="h-16 w-16 rounded-full bg-success/10 flex items-center justify-center">
                      <CheckCircle2 className="h-8 w-8 text-success" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Check your email</h3>
                    <p className="text-sm text-muted-foreground mt-2">
                      We've sent a password reset link to <strong>{email}</strong>.
                      The link will expire in 1 hour.
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Didn't receive the email? Check your spam folder or{' '}
                    <button
                      type="button"
                      className="text-primary hover:underline font-medium"
                      onClick={() => { setSent(false); setEmail(''); }}
                    >
                      try again
                    </button>
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="email">Email Address</Label>
                    <div className="relative mt-2">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="your.email@hospital.com"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setError(''); }}
                        className={`pl-10 ${error ? 'border-destructive' : ''}`}
                        autoFocus
                      />
                    </div>
                    {error && (
                      <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {error}
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground"
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </Button>
                </form>
              )}

              <div className="text-center pt-2">
                <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back to Sign In
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default ForgotPassword;
